(ns minorodata.components.forms.inputs
  (:require [reagent.core :as reagent]
            [re-posh.core :as re-posh]
            [clojure.string :as string]
            [minorodata.components.forms.doc :as docs]
            [ui.fields]
            [ui.datepicker]
            [ui.typography :as typography]
            ["react" :as react]
            ["react-dropzone" :default Dropzone :refer [useDropzone]]
            ["lucide-react" :refer [FileKey2 Files]]
            [minorodata.components.monaco.editor :as monaco.editor]
            [applied-science.js-interop :as js-interop]))


(defn block-wrapper [{:keys [doc input]}]
  (when input
    [:div {:class "w-full flex"}
     input
     (when doc
       doc)]))


(def input ui.fields/input)
(def select ui.fields/select)
(def checkbox ui.fields/checkbox)
(def switch ui.fields/switch)

(defn source-select [{:keys [field block fields errors]}]
  (let [{:keys [label name type required
                source-options-field options-name-path options-value-path]} block
        source-id   (get-in fields [:source-id :value])
        full-source @(re-posh/subscribe [:source/pull [:source/source-id source-id]])
        options     (into []
                          (map (fn [field]
                                 {:text  (get-in field options-name-path)
                                  :value (get-in field options-value-path)}))
                          (get-in full-source [:source/source source-options-field]))]

    [select
     {:field         field
      :errors        errors
      :id            name
      :variant       "standard"
      :type          type
      :required      required
      :native        false
      :options       options
      :disabled      false
      :label         label}]))


(defn file-handler [field files]
  (let [file      (first files)
        file-name (js-interop/get file :name)
        mime-type (js-interop/get file :type)
        file-ext  (last (string/split file-name #"\."))
        reader    (js/FileReader.)
        on-load   (fn [e]
                    (let [result (js-interop/get-in e [:target :result])]
                      ((:action field) {:content   result
                                        :mime-type mime-type
                                        :ext       file-ext
                                        :name      file-name})))]
    (js-interop/assoc! reader :onload on-load)
    (. reader (readAsText file))))

(defn wrap-with-doc [input doc]
  [block-wrapper {:doc [docs/tooltip {:doc       doc
                                      :align-top true}]
                  :input input}])


(defn advanced-options [{:keys [block
                                fields
                                multiple
                                errors
                                type
                                native
                                data-cy]}]
  (let [{advanced-options? :value} (get fields :advanced-options)
        excluded? #(contains? (set (:exclude block)) %)]
    [:div
     [checkbox {:label "Advanced Options"
                :field (get fields :advanced-options)
                :id :advanced-options
                :type :checkbox}]

     (when advanced-options?
       [:<>
        (when-not (or (excluded? :start-date)
                      (excluded? :end-date))
          [wrap-with-doc [:h3 "Backfill Options:"] "Date range options for the initial backfill."])
        (when-not (excluded? :start-date)
          [ui.datepicker/date-picker {:label "Start Date"
                                      :field (get fields :start-date)}])
        (when-not (excluded? :end-date)
          [ui.datepicker/date-picker {:label "End Date"
                                      :field (get fields :end-date)}])
        (when-not (excluded? :batch-size)
          [wrap-with-doc [:h3 "Batch Options:"] "These settings control the batch of data used in the ingest."])
        (when-not (excluded? :batch-size)
          [wrap-with-doc
           [select
            {:field         (get fields :batch-size)
             :multiple      multiple
             :errors        errors
             :id            :batch-size
             :variant       "standard"
             :type          type
             :native        native
             :label         "Batch Size"
             :data-cy       data-cy
             :options       (mapv (fn [option]
                                    {:value option :text (str option)})
                                  [5000
                                   10000
                                   25000
                                   50000
                                   100000
                                   200000])}]

           "The row total of the batch of data which is copied to the data warehouse at one time.
           For large datasets with many columns and large json fields it is best practise to reduce the batch size for a more reliable outcome."])])]))


(defn file-input-render [{:keys [field name required]}]

  (let [on-drop (react/useCallback (fn [accepted-files]
                                     (file-handler field accepted-files))
                                   #js [field])
        dz (useDropzone #js {:onDrop on-drop
                             :multiple false})
        get-input-props (js-interop/get dz :getInputProps)
        get-root-props  (js-interop/get dz :getRootProps)
        input-ref       (js-interop/get dz :inputRef)
        root-ref        (js-interop/get dz :rootRef)
        accepted-files  (js-interop/get dz :acceptedFiles)

        root-props  (js->clj (get-root-props) :keywordize-keys true)
        input-props (js->clj (get-input-props) :keywordize-keys true)

        empty-file? (empty? (:value field))
        file-name   (:name (:value field))]

    (react/useEffect
     (fn []
       (when-let [input-ref-current (js-interop/get input-ref :current)]
         (let [data-transfer (new js/DataTransfer)
               mock-file (new js/File
                              #js [""]
                              file-name
                              #js {:type "text/csv"})
               files (if (empty? accepted-files)
                       (when-not empty-file? [mock-file])
                       accepted-files)]

           (when-not (empty? files)
             (doall
              (for [file files]
                (js-interop/call-in data-transfer [:items :add] file)))

             (js-interop/assoc! input-ref-current :files (js-interop/get data-transfer :files))

             (when (js-interop/get-in input-ref-current [:webkitEntries :length])
               (js-interop/assoc-in! input-ref-current [:dataset :file] (js-interop/get (first (js-interop/get data-transfer :files)) :name))))))
       (fn []))
     #js [accepted-files])

    [:div (merge
           root-props
           {:class ["h-36 w-full border-dashed border-2 border-kleene-neutral-01 dark:border-kleene-dark-04"
                    "rounded-lg p-4 bg-white dark:bg-kleene-dark-02 flex flex-col items-center justify-center"
                    "text-center relative mt-4"]
            :ref root-ref})

     [:input
      (merge input-props
             {:name     name
              :required required
              :ref      input-ref
              :class    "absolute top-0 left-0 right-0 opacity-0"
              :style    {}})]

     (if empty-file?
       [:p "Drag 'n' drop your key file here, or click to select a file"]

       [:p
        "File Attached"
        (when (some? file-name)
          (str ": " file-name))])

     [:> FileKey2 {:class "opacity-50 mt-4" :size 48}]]))

(defn file-input [{:keys [field id errors required]}]
  (let [error (get-in errors [id :value])]

    [:div {:class "flex flex-col w-full"}
     [:f> file-input-render {:name            id
                             :field           field
                             :errors          errors
                             :required        required}]
     (when error
       [:div {:class "mt-4"}
        [:p {:class "text-kleene-error-01"}
         error]])]))


(defn editor [{:keys [field id label class color language custom-actions auto-focus selection-observer read-only controlled? required wrapper-class]
               :or   {custom-actions []
                      language       "sql"
                      read-only      false
                      controlled?    false}}]

  [:div {:class wrapper-class}
   (when label
     [typography/label {:for     id
                        :htmlFor id}
      label (when required " *")])

   [:div {:class class}
    [:f> monaco.editor/editor
     {:language          language
      :theme             "vs-dark"

      :id                id
      :on-change         (fn [value]
                           ((:action field) (clj->js {:target {:value value}})))

      :editor-will-mount (fn [])
      :editor-did-mount  (fn [editor _monaco]
                           (when auto-focus
                             (js-interop/call editor :focus))

                           (when selection-observer
                             (js-interop/call editor "onDidChangeCursorSelection" (partial selection-observer editor)))

                           (doall
                            (for [action custom-actions]
                              (js-interop/call editor :addAction (clj->js action)))))
      :options           {:fontSize        14
                          :readOnly        read-only
                          :automaticLayout true
                          :contextmenu     true}
      :controlled?       controlled?
      :value             (or (:value field) "")}]]])


(defn file-input-styled [{:keys [get-root-props get-input-props]}]
  [:div (->> (js->clj (get-root-props))
             (merge {:class
                     "cursor-pointer flex flex-col items-center p-10 border-2 rounded-xl border-dashed border-kleene-text-success/75 w-lg mx-auto bg-kleene-background gap-4"}))
   [:input (->> (js->clj (get-input-props))
                (merge {:data-cy "dropzone"}))]

   [:div {}
    [:> Files {:size 64
               :class "text-kleene-text-success"}]]

   [:p {:class "text-kleene-text-success"}
    "Drag 'n' drop your file here, or click to select a file"]])


(defn dropzone [{:keys [field id label class color handle-files-fn multiple required]
                 :or   {multiple        false
                        handle-files-fn (fn [_] js/console.log)}}]

  [:div {}

   (when label
     [typography/label {:for     id
                        :htmlFor id}
      label (when required " *")])

   [:div {:class class
          :id    id}
    [:> Dropzone {:multiple multiple
                  :onDrop   (handle-files-fn (:action field))}
     (fn [props]
       (let [getRootProps  (js-interop/get props :getRootProps)
             getInputProps (js-interop/get props :getInputProps)]
         (reagent/as-element
          [file-input-styled {:get-root-props  getRootProps
                              :get-input-props getInputProps}])))]]])
